/**
 * Client mirror of the server's strict `{{ name }}` automation templates
 * (`crates/mit-chat/src/templates.rs`): highlighting, validation and examples.
 */
import type { Param } from "@/api/generated/Param"

export const PARAM_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/
const PLACEHOLDER = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g
const BROKEN = /\{\{|\}\}/g

export type Segment =
  | { type: "text"; text: string }
  | { type: "placeholder"; text: string; name: string; status: "ok" | "unknown" | "file" }
  | { type: "broken"; text: string }

/** Splits a template into text, placeholders (with their validity) and stray braces. */
export function tokenize(template: string, schema: Param[]): Segment[] {
  const out: Segment[] = []
  const pushText = (text: string) => {
    let last = 0
    for (const m of text.matchAll(BROKEN)) {
      if (m.index > last) out.push({ type: "text", text: text.slice(last, m.index) })
      out.push({ type: "broken", text: m[0] })
      last = m.index + m[0].length
    }
    if (last < text.length) out.push({ type: "text", text: text.slice(last) })
  }
  let last = 0
  for (const m of template.matchAll(PLACEHOLDER)) {
    if (m.index > last) pushText(template.slice(last, m.index))
    const name = m[1]!
    const p = schema.find((x) => x.name === name)
    out.push({
      type: "placeholder",
      text: m[0],
      name,
      status: !p ? "unknown" : p.type === "file" ? "file" : "ok",
    })
    last = m.index + m[0].length
  }
  if (last < template.length) pushText(template.slice(last))
  return out
}

export type TemplateIssue =
  | { kind: "unknown"; name: string }
  | { kind: "file"; name: string }
  | { kind: "unterminated" }

export function templateIssues(template: string, schema: Param[]): TemplateIssue[] {
  const issues: TemplateIssue[] = []
  const seen = new Set<string>()
  for (const s of tokenize(template, schema)) {
    if (s.type === "placeholder" && s.status !== "ok" && !seen.has(s.name)) {
      seen.add(s.name)
      issues.push({ kind: s.status, name: s.name })
    }
    if (s.type === "broken" && !issues.some((i) => i.kind === "unterminated"))
      issues.push({ kind: "unterminated" })
  }
  return issues
}

/** Names used as placeholders (in order of first use). */
export function placeholdersIn(template: string): string[] {
  return [...new Set([...template.matchAll(PLACEHOLDER)].map((m) => m[1]!))]
}

export type ParamIssue = "name" | "duplicate"

/** Per-row problems in a parameter schema (index → issue). */
export function schemaIssues(schema: Param[]): Map<number, ParamIssue> {
  const issues = new Map<number, ParamIssue>()
  const seen = new Map<string, number>()
  schema.forEach((p, i) => {
    if (!PARAM_NAME.test(p.name)) issues.set(i, "name")
    else if (seen.has(p.name)) issues.set(i, "duplicate")
    else seen.set(p.name, i)
  })
  return issues
}

/** Turns a label into a parameter name: "Customer email" → "customer_email". */
export function slugifyParam(label: string): string {
  const s = label
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
  return /^[0-9]/.test(s) ? `p_${s}` : s
}

/** Renders a template with values, like the server (missing values stay visible). */
export function renderPreview(template: string, values: Record<string, string>): string {
  return template.replace(PLACEHOLDER, (all, name: string) =>
    values[name] !== undefined ? values[name]! : all,
  )
}

const shellQuote = (s: string) => `'${s.replace(/'/g, `'\\''`)}'`

/** Copy-paste curl for the automation webhook (JSON, or multipart when there are file params). */
export function automationCurl(url: string, schema: Param[], secret = "<secret>"): string {
  const files = schema.filter((p) => p.type === "file")
  const plain = schema.filter((p) => p.type !== "file")
  const lines = [`curl -X POST ${shellQuote(url)}`, `  -H ${shellQuote(`Authorization: Bearer ${secret}`)}`]
  const sample = (p: Param) => p.default || (p.type === "number" ? "1" : `<${p.name}>`)
  if (files.length) {
    for (const p of plain) lines.push(`  -F ${shellQuote(`${p.name}=${sample(p)}`)}`)
    for (const p of files) lines.push(`  -F ${shellQuote(`${p.name}=@./${p.name}.pdf`)}`)
  } else {
    const body: Record<string, string | number> = {}
    for (const p of plain) body[p.name] = p.type === "number" ? Number(sample(p)) : sample(p)
    lines.push(`  -H 'Content-Type: application/json'`, `  -d ${shellQuote(JSON.stringify(body))}`)
  }
  return lines.join(" \\\n")
}

/** Copy-paste curl for a chat endpoint (JSON or streaming). */
export function endpointCurl(
  url: string,
  opts: { stream: boolean; continuity: boolean; token?: string },
): string {
  const body: Record<string, string> = { message: "Hello!" }
  if (opts.continuity) body.context_id = "<conversation id>"
  const lines = [
    `curl ${opts.stream ? "-N " : ""}-X POST ${shellQuote(url)}`,
    `  -H ${shellQuote(`Authorization: Bearer ${opts.token ?? "<token>"}`)}`,
    `  -H 'Content-Type: application/json'`,
  ]
  if (opts.stream) lines.push(`  -H 'Accept: text/event-stream'`)
  lines.push(`  -d ${shellQuote(JSON.stringify(body))}`)
  return lines.join(" \\\n")
}
